import React, { useEffect, useMemo } from 'react';
import {
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableRow,
  Button,
  Stack,
  Section,
  IconButton,
  RemoveCircleOutlineIcon,
} from '@gogame/components';
import {
  FormField,
  withForm,
  FormFieldArray,
  FormFieldGroup,
  useSiblingFieldName,
} from '@gogame/form';

import { useQuery } from '@gogame/query';

const METRIC = 'metric';
const OPERATOR = 'operator';
const VALUE = 'value';
const ADVANCED_FILTERS_NAME = 'advancedFilters';

const required = (label) => (value) =>
  value ? undefined : `${label} is required`;

const stringifyFilterId = ({
  [METRIC]: metric,
  [OPERATOR]: operator,
  [VALUE]: value,
}) => `[${metric}]${operator}[${value}]`;

const parseFilterId = (id) => {
  const [_, metric, operator, value] = id.match(/\[(\w+)\](.+?)\[(.+?)\]/);
  return { [METRIC]: metric, [OPERATOR]: operator, [VALUE]: value };
};

const OperatorSelect = ({ ...props }) => {
  const getName = useSiblingFieldName(props);
  const depends = useMemo(() => {
    return [METRIC].map(getName);
  }, [getName]);

  return <FormField.Select {...props} depends={depends} />;
};

const AdvancedFiltersForm = withForm({
  enableReinitialize: true,
})(({
  submitForm,
  disabled,
  dirty,
  values,
  loadMetricsList,
  loadMetricsOperators,
  dimension,
}) => {
  const { data, isLoading } = useQuery(
    [ADVANCED_FILTERS_NAME, 'metrics', dimension],
    () => loadMetricsList({ dimension })
  );
  useEffect(() => {
    if (dirty) {
      submitForm();
    }
  }, [values]);
  return (
    <Section>
      <FormFieldArray name={ADVANCED_FILTERS_NAME}>
        {({ push, value, remove }) => (
          <Stack spacing={2} key={`filters`}>
            {value?.length ? (
              <TableContainer>
                <Table size={'small'}>
                  <TableHead>
                    <TableRow>
                      <TableCell col={'metric'} size={'medium'}>
                        Metric
                      </TableCell>
                      <TableCell col={'operator'} size={'medium'} width={194}>
                        Operator
                      </TableCell>
                      <TableCell col={'value'} size={'medium'} width={194}>
                        Value
                      </TableCell>
                      <TableCell
                        col={'actions'}
                        size={'medium'}
                        width={64}
                        padding={'actions'}
                      />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {value?.map((_, i) => {
                      const n = `${ADVANCED_FILTERS_NAME}[${i}]`;
                      return (
                        <FormFieldGroup name={n} key={n}>
                          <TableRow>
                            <TableCell col={'metric'} size={'medium'}>
                              <FormField.Select
                                disabled={disabled}
                                groupBy={'groupName'}
                                name={METRIC}
                                padding={0}
                                options={data}
                                loading={isLoading}
                                disableError={true}
                                validate={required('Metric')}
                              />
                            </TableCell>
                            <TableCell
                              col={'operator'}
                              size={'medium'}
                              width={194}
                            >
                              <OperatorSelect
                                disabled={disabled}
                                withFilter={false}
                                disableError={true}
                                name={OPERATOR}
                                source={loadMetricsOperators}
                                padding={0}
                                validate={required('Operator')}
                              />
                            </TableCell>
                            <TableCell
                              col={'value'}
                              size={'medium'}
                              width={194}
                            >
                              <FormField.TextField
                                disabled={disabled}
                                padding={0}
                                name={VALUE}
                                validate={required('Value')}
                                disableError={true}
                              />
                            </TableCell>
                            <TableCell
                              col={'actions'}
                              size={'medium'}
                              width={64}
                              padding={'actions'}
                            >
                              <IconButton
                                icon={<RemoveCircleOutlineIcon />}
                                tooltip={'Remove'}
                                onClick={() => remove(i)}
                                disabled={disabled}
                              />
                            </TableCell>
                          </TableRow>
                        </FormFieldGroup>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
            <div>
              <Button
                variant={'outlined'}
                add
                onClick={() => push({})}
                disabled={disabled}
              >
                Filter
              </Button>
            </div>
          </Stack>
        )}
      </FormFieldArray>
    </Section>
  );
});

const AdvancedFilters = ({ onChange, values, ...props }) => {
  const initialValues = useMemo(() => {
    if (values) {
      return { [ADVANCED_FILTERS_NAME]: values.map(parseFilterId) };
    }
  }, [values]);

  const handleSubmit = (values) => {
    const advancedFilters = values?.[ADVANCED_FILTERS_NAME];
    return onChange(advancedFilters?.map(stringifyFilterId));
  };

  return (
    <AdvancedFiltersForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      {...props}
    />
  );
};

export default AdvancedFilters;
